import { ReactNode, useState } from "react";
import { buildClasses } from "../utils/buildClasses";
import { RyeMainContentCard } from "../rye/RyeMainContentCard";
import { RyeScrollListener } from "../rye/RyeMainContentScrollShadow";
import { BudgetTracker } from "./BudgetTracker";
import { createContextWithSetter } from "../utils/ContextUtils";
import { RyeMainContentSplitPane } from "../rye/RyeMainContentSplitPane";
import { TransactionsDrilldownPane } from "../common/TransactionsDrilldownPane";
import { BreadTransaction, BreadBudget } from "breadcommon";
import { RyeButtonMenu } from "../rye/RyeButtonMenu";
import { RyeToggle } from "../rye/RyeToggle";
import { RyeSegmentedButton } from "../rye/RyeSegmentedButton";
import { BudgetBarScale } from "./budgetBarRows";
import {
  BudgetTrackingBars,
  BudgetTrackingBarsControls,
  BudgetTypeFilter,
} from "./BudgetTrackingBars";
import { CategoryTypeTotals } from "./CategoryTypeTotals";

export const SelectedTransactionsContext = createContextWithSetter<
  BreadTransaction[] | null
>(null);

export const CellSelectionContext = createContextWithSetter<string | null>(
  null,
);

export interface ColumnVisibility {
  totalBudgeted: boolean;
  budgetedToDate: boolean;
  actualsToDate: boolean;
  monthlyActuals: boolean;
}

export type BudgetTrackingViewMode = "table" | "graph";

/**
 * Tracks actuals against `budget`: the budget's header, its totals, then the
 * chart or table with its controls right above it. The parent remounts this
 * (via `key`) when the budget or view mode changes, which also clears any
 * open transaction drilldown.
 */
export function BudgetTrackingReport({
  budget,
  header,
  viewMode,
  onViewModeChange,
  columnVisibility,
  setColumnVisibility,
  onScroll,
}: {
  budget: BreadBudget;
  /** The budget's name, actions, and months, at the top of the report. */
  header: ReactNode;
  viewMode: BudgetTrackingViewMode;
  onViewModeChange: (viewMode: BudgetTrackingViewMode) => void;
  columnVisibility: ColumnVisibility;
  setColumnVisibility: (
    update: (prev: ColumnVisibility) => ColumnVisibility,
  ) => void;
  /** Called as the report's body scrolls, e.g. to shade the top bar. */
  onScroll: (body: HTMLElement) => void;
}): JSX.Element {
  const [selectedTransactions, setSelectedTransactions] = useState<
    BreadTransaction[] | null
  >(null);
  const [selectedCellId, setSelectedCellId] = useState<string | null>(null);
  // The graph names what was clicked; the table's cells don't say.
  const [selectedLabel, setSelectedLabel] = useState("Selected");
  const [barsType, setBarsType] = useState<BudgetTypeFilter>("all");
  const [barsScale, setBarsScale] = useState<BudgetBarScale>("dollars");

  // A selection made in one view doesn't carry over to the other.
  function switchViewMode(mode: BudgetTrackingViewMode) {
    clearSelectedTransactions();
    onViewModeChange(mode);
  }

  const viewSwitch = (
    <RyeSegmentedButton
      entries={[
        {
          id: "graph",
          icon: "bar_chart",
          displayText: "Chart",
          onClick: () => switchViewMode("graph"),
        },
        {
          id: "table",
          icon: "table",
          displayText: "Table",
          onClick: () => switchViewMode("table"),
        },
      ]}
      initEntryId={viewMode}
    />
  );

  function clearSelectedTransactions() {
    setSelectedTransactions(null);
    setSelectedCellId(null);
  }

  return (
    <SelectedTransactionsContext.Provider
      value={[selectedTransactions, setSelectedTransactions]}
    >
      <CellSelectionContext.Provider
        value={[selectedCellId, setSelectedCellId]}
      >
        <RyeMainContentSplitPane
          bottomPaneLabel="transactions pane"
          bottomPane={
            selectedTransactions !== null ? (
              <TransactionsDrilldownPane
                onClose={clearSelectedTransactions}
                groupLabel={selectedLabel}
                transactions={selectedTransactions}
              />
            ) : null
          }
        >
          <RyeMainContentCard
            roundedTl={false}
            roundedTr={false}
            roundedBl={selectedTransactions === null}
            roundedBr={selectedTransactions === null}
          >
            <RyeScrollListener onScroll={onScroll} />
            {header}
            <CategoryTypeTotals
              beginDate={budget.beginDateTimestampSecs}
              endDate={budget.endDateTimestampSecs}
              budget={budget}
            />
            <div className={buildClasses("flex", "flex-col", "gap-4", "pt-6")}>
              {/* One row for both views, so the switch stays mounted and slides. */}
              <div className={buildClasses("flex", "items-center", "gap-3")}>
                {viewSwitch}
                <div className={buildClasses("ml-auto")}>
                  {viewMode === "table" ? (
                    <ColumnVisibilityMenu
                      columnVisibility={columnVisibility}
                      setColumnVisibility={setColumnVisibility}
                    />
                  ) : (
                    <BudgetTrackingBarsControls
                      type={barsType}
                      setType={setBarsType}
                      scale={barsScale}
                      setScale={setBarsScale}
                    />
                  )}
                </div>
              </div>
              {viewMode === "table" ? (
                <BudgetTracker
                  budget={budget}
                  columnVisibility={columnVisibility}
                />
              ) : (
                <BudgetTrackingBars
                  budget={budget}
                  type={barsType}
                  scale={barsScale}
                  selectedId={selectedCellId}
                  onSelectRow={(row) => {
                    setSelectedCellId(row.id);
                    setSelectedLabel(row.label);
                    setSelectedTransactions(row.transactions);
                  }}
                />
              )}
            </div>
          </RyeMainContentCard>
        </RyeMainContentSplitPane>
      </CellSelectionContext.Provider>
    </SelectedTransactionsContext.Provider>
  );
}

/** The table view's column toggles. */
export function ColumnVisibilityMenu({
  columnVisibility,
  setColumnVisibility,
}: {
  columnVisibility: ColumnVisibility;
  setColumnVisibility: (
    update: (prev: ColumnVisibility) => ColumnVisibility,
  ) => void;
}): JSX.Element {
  return (
    <RyeButtonMenu
      text="Columns"
      icon="view_column"
      size="sm"
      variant="outlined"
      vibe="subdued"
      align="right"
      tooltip="Choose which columns to show"
      menuEntries={[
        {
          content: (
            <div className={buildClasses("flex", "items-center", "gap-3")}>
              <RyeToggle
                size="sm"
                initIsChecked={columnVisibility.totalBudgeted}
                onChange={(checked) =>
                  setColumnVisibility((prev) => ({
                    ...prev,
                    totalBudgeted: checked,
                  }))
                }
              />
              <span>Total Budgeted</span>
            </div>
          ),
          closeOnClick: false,
        },
        {
          content: (
            <div className={buildClasses("flex", "items-center", "gap-3")}>
              <RyeToggle
                size="sm"
                initIsChecked={columnVisibility.budgetedToDate}
                onChange={(checked) =>
                  setColumnVisibility((prev) => ({
                    ...prev,
                    budgetedToDate: checked,
                  }))
                }
              />
              <span>Budgeted to Date</span>
            </div>
          ),
          closeOnClick: false,
        },
        {
          content: (
            <div className={buildClasses("flex", "items-center", "gap-3")}>
              <RyeToggle
                size="sm"
                initIsChecked={columnVisibility.actualsToDate}
                onChange={(checked) =>
                  setColumnVisibility((prev) => ({
                    ...prev,
                    actualsToDate: checked,
                  }))
                }
              />
              <span>Actuals to Date</span>
            </div>
          ),
          closeOnClick: false,
        },
        {
          content: (
            <div className={buildClasses("flex", "items-center", "gap-3")}>
              <RyeToggle
                size="sm"
                initIsChecked={columnVisibility.monthlyActuals}
                onChange={(checked) =>
                  setColumnVisibility((prev) => ({
                    ...prev,
                    monthlyActuals: checked,
                  }))
                }
              />
              <span>Monthly Actuals</span>
            </div>
          ),
          closeOnClick: false,
        },
      ]}
    />
  );
}
